import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { CheckCircle, X, ArrowRight, Star, Zap, Building2, Users, Phone, Calendar, Shield, CreditCard, Clock, HelpCircle, ChevronDown, ChevronUp, TrendingUp } from 'lucide-react';

const Pricing = () => {
  const [expandedFaq, setExpandedFaq] = useState(null);
  
  const handleBookDemo = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const handleStartTrial = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const plans = [
    {
      id: 1,
      name: "Growth Accelerator",
      monthlyPrice: "$397",
      setupFee: "$1,197",
      setupTime: "2-3 weeks",
      bestFor: "Existing website",
      icon: Zap,
      gradient: "from-forest-primary to-forest-primary-600",
      features: {
        isabellaAI: true,
        reviewAccelerator: true,
        localSEO: true,
        growthCenter: true,
        customWebsite: false,
        prioritySupport: false,
        dedicatedManager: false,
        phoneSupport: false
      }
    },
    {
      id: 2,
      name: "Complete Restaurant System", 
      monthlyPrice: "$597",
      setupFee: "$2,497",
      setupNote: "waived annually",
      setupTime: "2-3 weeks",
      bestFor: "Full transformation",
      icon: Building2,
      gradient: "from-forest-primary to-sienna-accent",
      popular: true,
      features: {
        isabellaAI: true,
        reviewAccelerator: true,
        localSEO: true,
        growthCenter: true,
        customWebsite: true,
        prioritySupport: true,
        dedicatedManager: false,
        phoneSupport: false
      }
    },
    {
      id: 3,
      name: "Multi-Location",
      monthlyPrice: "Custom",
      setupFee: "Custom",
      setupTime: "3-4 weeks",
      bestFor: "3+ locations",
      icon: Users,
      gradient: "from-brand-gold to-brand-gold-600",
      features: {
        isabellaAI: true,
        reviewAccelerator: true,
        localSEO: true,
        growthCenter: true,
        customWebsite: true,
        prioritySupport: true,
        dedicatedManager: true,
        phoneSupport: true
      }
    }
  ];

  const includedFeatures = [
    {
      title: "Isabella AI Integration",
      description: "24/7 reservation assistant, multilingual support, instant FAQ answers",
      icon: Star
    },
    {
      title: "Review Automation",
      description: "10-15 new reviews monthly, AI-drafted responses, reputation monitoring", 
      icon: Star
    },
    {
      title: "Local SEO Optimization",
      description: "Google Business Profile mastery, citation building, competitor tracking",
      icon: Star
    },
    {
      title: "Growth Command Center",
      description: "Unified analytics, automated reports, real-time insights",
      icon: Star
    },
    {
      title: "Training & Onboarding",
      description: "We handle everything—setup, integration, and training",
      icon: Star
    },
    {
      title: "30-Day Money-Back Guarantee",
      description: "Risk-free trial. Cancel anytime in first 30 days for full refund.",
      icon: Shield
    }
  ];

  const notIncluded = [
    {
      title: "Third-Party Booking Fees",
      description: "OpenTable, Resy, and other platforms charge their own fees (typically $1-2 per reservation)"
    },
    {
      title: "Professional Photography", 
      description: "Food photography, interior shots, team photos (recommended but not required; we can work with smartphone photos)"
    },
    {
      title: "POS System Integration",
      description: "We integrate with Toast, Square, Clover, Lightspeed at no extra charge, but you need active subscription with them"
    }
  ];

  const faqItems = [
    {
      question: "What happens after 30 days if I'm not satisfied?",
      answer: "Full refund. We process it within 3 business days."
    },
    {
      question: "Can I switch plans later?",
      answer: "Yes. You can upgrade anytime with immediate activation. Price difference prorated."
    },
    {
      question: "What if I need custom features?",
      answer: "Custom development available starting at $150/hour. Most restaurants don't need it."
    },
    {
      question: "Do you offer payment plans?",
      answer: "Yes, for Complete Restaurant System. $997 down, then $722/month for 12 months."
    }
  ];

  return (
    <div className="pt-16 overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen bg-gradient-to-br from-restaurant-dark-900 via-restaurant-dark-800 to-restaurant-dark-700">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-br from-forest-primary/20 to-forest-primary-600/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 left-20 w-64 h-64 bg-gradient-to-br from-sienna-accent/20 to-brand-gold/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold text-white mb-8 leading-tight">
            Simple, Transparent
            <span className="block bg-gradient-to-r from-forest-primary to-forest-primary-600 bg-clip-text text-transparent">
              Pricing
            </span>
          </h1>
          <p className="text-lg sm:text-xl text-brand-ivory/90 max-w-4xl mx-auto mb-12 leading-relaxed">
            30-day money-back guarantee on all plans. No credit card required to start.
          </p>
          
          {/* Trust badges */}
          <div className="flex flex-wrap justify-center items-center gap-4 sm:gap-8 mb-16">
            <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-md rounded-full px-4 sm:px-6 py-3 border border-white/20">
              <Shield className="text-forest-primary" size={20} />
              <span className="text-white font-medium">30-Day Money-Back</span>
            </div>
            <div className="flex items-center space-x-3 bg-white/10 backdrop-blur-md rounded-full px-4 sm:px-6 py-3 border border-white/20">
              <CreditCard className="text-forest-primary" size={20} />
              <span className="text-white font-medium">No Credit Card Required</span>
            </div>
          </div>

          {/* Enhanced bottom section with plans preview */}
          <div className="mt-20 space-y-12">
            {/* Plan preview cards */}
            <div className="flex justify-center items-center space-x-4 sm:space-x-8">
              {plans.slice(0, 3).map((plan, index) => (
                <div key={plan.id} className="group cursor-pointer">
                  <div className={`relative w-20 h-28 sm:w-24 sm:h-32 bg-gradient-to-br ${plan.gradient} rounded-2xl backdrop-blur-sm border border-white/20 flex flex-col items-center justify-center transform group-hover:scale-110 group-hover:-translate-y-2 transition-all duration-500 shadow-lg ${plan.popular ? 'scale-110' : ''}`}>
                    {plan.popular && (
                      <div className="absolute -top-2 left-1/2 transform -translate-x-1/2">
                        <Badge className="bg-white text-sienna-accent text-xs px-2 py-1 font-semibold">Popular</Badge>
                      </div>
                    )}
                    <plan.icon className="text-white mb-2" size={24} />
                    <div className="text-white text-xs font-bold text-center px-2">{plan.monthlyPrice}</div>
                    <div className="text-white/80 text-xs text-center">{plan.name.split(' ')[0]}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Stats section */}
            <div className="grid grid-cols-3 gap-8 max-w-2xl mx-auto">
              {[
                { number: "50+", label: "Restaurants Growing", icon: Users, color: "text-forest-primary" },
                { number: "340%", label: "Avg Revenue Increase", icon: TrendingUp, color: "text-sienna-accent" },
                { number: "24/7", label: "AI Assistant Active", icon: Clock, color: "text-forest-primary" }
              ].map((stat, index) => (
                <div key={index} className="text-center group">
                  <div className="w-14 h-14 bg-white/10 backdrop-blur-md border border-white/20 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:bg-white/20 transition-all duration-300">
                    <stat.icon className={stat.color} size={20} />
                  </div>
                  <div className="text-3xl font-bold text-white mb-1">{stat.number}</div>
                  <div className="text-brand-ivory/80 text-sm">{stat.label}</div>
                </div>
              ))}
            </div>

            {/* Scroll indicator */}
            <div className="flex justify-center">
              <div className="flex items-center space-x-8">
                <div className="w-16 h-px bg-gradient-to-r from-transparent via-brand-ivory/40 to-transparent"></div>
                <div className="text-brand-ivory/60 text-sm font-medium">Scroll to compare plans</div>
                <div className="w-16 h-px bg-gradient-to-r from-transparent via-brand-ivory/40 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Comparison Table */}
      <section className="py-32 bg-gradient-to-br from-brand-ivory via-brand-ivory-50 to-brand-ivory relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-gradient-to-br from-forest-primary/10 to-forest-primary-600/10 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-gradient-to-br from-sienna-accent/10 to-brand-gold/10 rounded-full blur-3xl"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold text-brand-charcoal mb-6">
              Choose Your Plan
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-brand-charcoal-600 max-w-3xl mx-auto">
              All plans include core features. The only difference is whether you need a custom website.
            </p>
          </div>

          {/* Comparison Table */}
          <Card className="overflow-hidden shadow-2xl border-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gradient-to-r from-restaurant-dark-900 to-restaurant-dark-800 text-white">
                    <th className="text-left py-4 sm:py-6 px-3 sm:px-6 font-bold text-sm sm:text-base lg:text-lg">Feature</th>
                    <th className="text-center py-4 sm:py-6 px-2 sm:px-6">
                      <div className="flex flex-col items-center space-y-2">
                        <Zap className="text-forest-primary" size={24} />
                        <span className="font-bold text-sm sm:text-base lg:text-lg">Growth Accelerator</span>
                      </div>
                    </th>
                    <th className="text-center py-4 sm:py-6 px-2 sm:px-6 relative">
                      <div className="flex flex-col items-center space-y-2">
                        <Building2 className="text-sienna-accent" size={24} />
                        <span className="font-bold text-sm sm:text-base lg:text-lg">Complete Restaurant System</span>
                        <Badge className="bg-sienna-accent text-white text-xs px-2 py-1">Most Popular</Badge>
                      </div>
                    </th>
                    <th className="text-center py-4 sm:py-6 px-2 sm:px-6">
                      <div className="flex flex-col items-center space-y-2">
                        <Users className="text-brand-gold" size={24} />
                        <span className="font-bold text-sm sm:text-base lg:text-lg">Multi-Location</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-ivory-200">
                  <tr className="hover:bg-brand-ivory-50/50 transition-colors">
                    <td className="py-3 sm:py-4 px-2 sm:px-6 font-semibold text-sm sm:text-base text-brand-charcoal">Monthly Price</td>
                    <td className="py-3 sm:py-4 px-2 sm:px-6 text-center">
                      <div className="text-lg sm:text-xl lg:text-2xl font-bold text-brand-charcoal">$397</div>
                    </td>
                    <td className="py-3 sm:py-4 px-2 sm:px-6 text-center bg-sienna-accent/5">
                      <div className="text-lg sm:text-xl lg:text-2xl font-bold text-brand-charcoal">$597</div>
                    </td>
                    <td className="py-3 sm:py-4 px-2 sm:px-6 text-center">
                      <div className="text-lg sm:text-xl lg:text-2xl font-bold text-brand-charcoal">Custom</div>
                    </td>
                  </tr>
                  <tr className="hover:bg-brand-ivory-50/50 transition-colors">
                    <td className="py-4 px-6 font-semibold text-brand-charcoal">Setup Fee</td>
                    <td className="py-4 px-6 text-center">
                      <div className="text-lg text-brand-charcoal-600">$1,197</div>
                    </td>
                    <td className="py-4 px-6 text-center bg-sienna-accent/5">
                      <div className="text-lg text-brand-charcoal-600">$2,497</div>
                      <div className="text-sm text-brand-charcoal-500">(waived annually)</div>
                    </td>
                    <td className="py-4 px-6 text-center">
                      <div className="text-lg text-brand-charcoal-600">Custom</div>
                    </td>
                  </tr>
                  <tr className="hover:bg-brand-ivory-50/50 transition-colors">
                    <td className="py-4 px-6 font-semibold text-brand-charcoal">Setup Time</td>
                    <td className="py-4 px-6 text-center text-brand-charcoal-600">2-3 weeks</td>
                    <td className="py-4 px-6 text-center text-brand-charcoal-600 bg-sienna-accent/5">2-3 weeks</td>
                    <td className="py-4 px-6 text-center text-brand-charcoal-600">3-4 weeks</td>
                  </tr>
                  {[
                    { feature: "Isabella AI Assistant", all: true },
                    { feature: "Review Accelerator", all: true },
                    { feature: "Local SEO Engine", all: true },
                    { feature: "Growth Command Center", all: true },
                    { feature: "Custom Website", basic: false, complete: true, multi: true },
                    { feature: "Priority Support", basic: false, complete: true, multi: true },
                    { feature: "Dedicated Manager", basic: false, complete: false, multi: true },
                    { feature: "Phone Support", basic: false, complete: false, multi: true }
                  ].map((row, index) => (
                    <tr key={index} className="hover:bg-brand-ivory-50/50 transition-colors">
                      <td className="py-4 px-6 font-medium text-brand-charcoal">{row.feature}</td>
                      <td className="py-4 px-6 text-center">
                        {row.all || row.basic !== false ? (
                          <CheckCircle className="text-forest-primary mx-auto" size={20} />
                        ) : (
                          <span className="text-brand-charcoal-400">—</span>
                        )}
                      </td>
                      <td className="py-4 px-6 text-center bg-sienna-accent/5">
                        {row.all || row.complete !== false ? (
                          <CheckCircle className="text-forest-primary mx-auto" size={20} />
                        ) : (
                          <span className="text-brand-charcoal-400">—</span>
                        )}
                      </td>
                      <td className="py-4 px-6 text-center">
                        {row.all || row.multi !== false ? (
                          <CheckCircle className="text-forest-primary mx-auto" size={20} />
                        ) : (
                          <span className="text-brand-charcoal-400">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                  <tr className="hover:bg-brand-ivory-50/50 transition-colors">
                    <td className="py-4 px-6 font-semibold text-brand-charcoal">Best For</td>
                    <td className="py-4 px-6 text-center text-brand-charcoal-600 text-sm">Existing website</td>
                    <td className="py-4 px-6 text-center text-brand-charcoal-600 text-sm bg-sienna-accent/5">Full transformation</td>
                    <td className="py-4 px-6 text-center text-brand-charcoal-600 text-sm">3+ locations</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </Card>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row justify-center items-center gap-6 mt-12">
            <Button 
              onClick={handleStartTrial}
              className="group relative font-bold text-lg px-10 py-4 rounded-xl shadow-2xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-[1.02] border-0 overflow-hidden"
              style={{
                backgroundColor: '#D4623C',
                color: '#FFFFFF',
                boxShadow: '0 25px 50px -12px rgba(212, 98, 60, 0.25)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#B8532F';
                e.target.style.boxShadow = '0 25px 50px -12px rgba(212, 98, 60, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#D4623C';
                e.target.style.boxShadow = '0 25px 50px -12px rgba(212, 98, 60, 0.25)';
              }}
            >
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 ease-in-out"></div>
              <div className="relative z-10 flex items-center justify-center space-x-2">
                <Zap className="w-5 h-5 group-hover:animate-pulse" />
                <span>Start 30-Day Risk-Free Trial</span>
              </div>
            </Button>
            <Button 
              onClick={handleBookDemo}
              variant="outline"
              className="font-semibold text-lg px-10 py-4 rounded-xl shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-[1.02]"
              style={{
                backgroundColor: 'transparent',
                border: '2px solid #2D6A4F',
                color: '#2D6A4F'
              }}
              onMouseEnter={(e) => {
                e.target.style.borderColor = '#1F4A36';
                e.target.style.backgroundColor = 'rgba(45, 106, 79, 0.1)';
              }}
              onMouseLeave={(e) => {
                e.target.style.borderColor = '#2D6A4F';
                e.target.style.backgroundColor = 'transparent';
              }}
            >
              Watch Demo
            </Button>
          </div>
        </div>
      </section>

      {/* What's Included Section */}
      <section className="py-32 bg-brand-ivory-50 relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-6xl font-bold text-brand-charcoal mb-6 leading-tight">
              What's Included in
              <span className="block bg-gradient-to-r from-forest-primary to-forest-primary-600 bg-clip-text text-transparent mt-2">
                Every Plan
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {includedFeatures.map((feature, index) => (
              <Card key={index} className="border-0 shadow-xl hover:shadow-2xl transition-all duration-500 transform hover:-translate-y-2 bg-white">
                <CardHeader>
                  <div className="flex items-center space-x-4 mb-4">
                    <div className="w-16 h-16 bg-gradient-to-br from-forest-primary to-forest-primary-600 rounded-2xl flex items-center justify-center">
                      <CheckCircle className="text-white" size={24} />
                    </div>
                    <CardTitle className="text-xl text-brand-charcoal">{feature.title}</CardTitle>
                  </div>
                  <CardDescription className="text-brand-charcoal-600 leading-relaxed">
                    {feature.description}
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What's NOT Included */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-brand-charcoal mb-6">
              What's NOT Included
            </h2>
            <p className="text-base sm:text-lg md:text-xl text-brand-charcoal-600 max-w-3xl mx-auto">
              Transparent about additional costs you may encounter
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {notIncluded.map((item, index) => (
              <Card key={index} className="border-2 border-sienna-accent/30 bg-sienna-accent/10 hover:shadow-lg transition-all duration-300">
                <CardHeader>
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-12 h-12 bg-sienna-accent/20 rounded-xl flex items-center justify-center">
                      <X className="text-sienna-accent" size={20} />
                    </div>
                    <CardTitle className="text-lg text-brand-charcoal">{item.title}</CardTitle>
                  </div>
                  <CardDescription className="text-brand-charcoal-600">
                    {item.description}
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Payment Options */}
      <section className="py-32 bg-gradient-to-br from-brand-ivory-50 to-brand-ivory-100">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-brand-charcoal mb-6">
              Payment Options
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="border-0 shadow-xl bg-white">
              <CardHeader className="text-center pb-8">
                <div className="w-16 h-16 bg-gradient-to-br from-forest-primary to-forest-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Calendar className="text-white" size={28} />
                </div>
                <CardTitle className="text-2xl text-brand-charcoal mb-4">Monthly</CardTitle>
                <CardDescription className="text-brand-charcoal-600">
                  Pay month-to-month. Cancel anytime with 30 days' notice. No long-term commitment.
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-0 shadow-xl bg-gradient-to-br from-forest-primary/10 to-forest-primary/20 border-2 border-forest-primary/30">
              <CardHeader className="text-center pb-8">
                <Badge className="bg-forest-primary text-white mb-4">Save $1,194</Badge>
                <div className="w-16 h-16 bg-gradient-to-br from-forest-primary to-forest-primary-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <Star className="text-white" size={28} />
                </div>
                <CardTitle className="text-2xl text-brand-charcoal mb-4">Annual (Save $1,194 + Waived Setup Fee)</CardTitle>
                <CardDescription className="text-brand-charcoal-600">
                  Pay annually and save 2 months. Setup fee waived on Complete Restaurant System (save $2,497). Locked-in pricing—no increases for 12 months.
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-32 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-brand-charcoal mb-6">
              Frequently Asked
              <span className="block bg-gradient-to-r from-forest-primary to-forest-primary-600 bg-clip-text text-transparent">
                Pricing Questions
              </span>
            </h2>
          </div>

          <div className="space-y-4">
            {faqItems.map((faq, index) => (
              <Card key={index} className="border border-brand-charcoal/20">
                <CardHeader
                  className="cursor-pointer hover:bg-brand-ivory-50 transition-colors duration-200"
                  onClick={() => setExpandedFaq(expandedFaq === index ? null : index)}
                >
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg text-brand-charcoal">{faq.question}</CardTitle>
                    {expandedFaq === index ? (
                      <ChevronUp className="text-brand-charcoal-600" size={24} />
                    ) : (
                      <ChevronDown className="text-brand-charcoal-600" size={24} />
                    )}
                  </div>
                </CardHeader>
                {expandedFaq === index && (
                  <CardContent className="pt-0">
                    <p className="text-brand-charcoal-600">{faq.answer}</p>
                  </CardContent>
                )}
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-32 bg-gradient-to-r from-restaurant-dark-900 to-restaurant-dark-800">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl lg:text-6xl font-bold text-white mb-8">
            Start Your 30-Day
            <span className="block bg-gradient-to-r from-forest-primary to-forest-primary-600 bg-clip-text text-transparent">
              Risk-Free Trial
            </span>
          </h2>
          <p className="text-xl text-brand-ivory/90 mb-12 leading-relaxed">
            Join restaurants already growing with PrismoSpark. No credit card required.
          </p>
          
          <Button 
            onClick={handleStartTrial}
            className="group relative font-bold text-xl px-12 py-6 rounded-2xl shadow-2xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-[1.02] overflow-hidden"
            style={{
              backgroundColor: '#D4623C',
              color: '#FFFFFF',
              boxShadow: '0 25px 50px -12px rgba(212, 98, 60, 0.25)'
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = '#B8532F';
              e.target.style.boxShadow = '0 25px 50px -12px rgba(212, 98, 60, 0.4)';
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = '#D4623C';
              e.target.style.boxShadow = '0 25px 50px -12px rgba(212, 98, 60, 0.25)';
            }}
          >
            <div className="relative z-10 flex items-center justify-center space-x-3">
              <Zap className="w-6 h-6" />
              <span>Start 30-Day Risk-Free Trial</span>
              <ArrowRight className="w-5 h-5" />
            </div>
          </Button>
          
          <p className="text-brand-ivory/70 mt-6 text-sm">
            Risk-free • No credit card required • Full refund within 30 days
          </p>
        </div>
      </section>

      {/* Contact Section Placeholder for Scroll Target */}
      <section id="contact" className="py-20 bg-gradient-to-br from-brand-ivory-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-5xl font-bold text-brand-charcoal mb-8">
            Ready to Get Started?
          </h2>
          <p className="text-xl text-brand-charcoal-600 mb-8 leading-relaxed">
            Contact us to start your trial or book a demo today.
          </p>
          <div className="bg-white p-8 rounded-2xl shadow-2xl border border-brand-charcoal/20">
            <p className="text-lg text-brand-charcoal-600 mb-4">
              📞 Phone: <span className="font-semibold">(555) 123-GROW</span>
            </p>
            <p className="text-lg text-brand-charcoal-600 mb-4">
              ✉️ Email: <span className="font-semibold">hello@prismospark.com</span>
            </p>
            <p className="text-base text-brand-charcoal-500">
              Our team is available Monday-Friday, 9 AM - 6 PM EST
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Pricing;